<template>
  <div class="nov-shopping" :class="cssType">
    <div class="nov-shopping-inner">
      <AppLang class="app-lang" />
      <MainHeader
        :server-ms="serverMs"
        :start-ms="startMs"
        :end-ms="endMs"
      />
      <div class="nov-shopping-content">
        <div class="pc-content-region shopping-region">
          <div v-if="!isMobile" class="pc-countdown-wrap">
            <ActCountDown
              :server-ms="serverMs"
              :start-ms="startMs"
              :end-ms="endMs"
            />
          </div>
          <ValidateUser id="nov-shopping-verify" />

          <GoodsList
            :list="gridGoods"
            :daily-list="dailyGoods"
            :status-map="goodsStatusMap"
            :voucher-balance="voucherBalance"
            :logged-in="loggedIn"
            :activity-phase="activityPhase"
            @exchange="handleExchange"
            @open-record="openPurchaseRecord"
            @top-up="goRecharge"
            @login-required="scrollToVerify"
            @not-enough="showNotEnough"
          />
        </div>

        <div class="pc-content-region milestone-region">
          <Milestone
            :list="milestoneList"
            :voucher-spent="voucherSpent"
            :logged-in="loggedIn"
            :activity-phase="activityPhase"
            @claim="handleClaim"
            @claim-all="handleClaimAll"
            @open-record="openMilestoneRecord"
            @top-up="goRecharge"
            @login-required="scrollToVerify"
          />
        </div>

        <div class="pc-content-region rule-region">
          <Rule />
        </div>
      </div>
    </div>

    <AppFooter />
    <ActFloat @top-up="goRecharge" />

    <RecordDialog v-model="recordDialog.visible" :type="recordDialog.type" :list="recordDialog.list" />
    <ResultDialog
      v-model="resultDialog.visible"
      :type="resultDialog.type"
      :prop="resultDialog.prop"
      :reward-list="resultDialog.rewards"
      @top-up="goRecharge"
      @open-record="openMilestoneRecord"
    />
    <ActTimeDialog
      v-model="actTimeDialog.visible"
      :phase="actTimeDialog.phase"
      :server-ms="serverMs"
      :start-ms="startMs"
    />
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { isMobile } from '@/utils'
import { useLog } from '@/composables/useLog'
import { localeToRegionIdMap } from '@/config/const'
import AppLang from '@/components/AppLang'
import AppFooter from '@/components/AppFooter'
import NovShoppingApi from '@/config/novShoppingActApi'
import MainHeader from './components/MainHeader/index.vue'
import ActCountDown from './components/ActCountDown/index.vue'
import ValidateUser from './components/ValidateUser/index.vue'
import GoodsList from './components/GoodsList/index.vue'
import Milestone from './components/Milestone/index.vue'
import Rule from './components/Rule/index.vue'
import ActFloat from './components/ActFloat/index.vue'
import RecordDialog from './components/RecordDialog/index.vue'
import ResultDialog from './components/ResultDialog/index.vue'
import ActTimeDialog from './components/ActTimeDialog/index.vue'
import { parseActTimeToMs, gmt7DateKey } from './utils/time'
import { getPropName } from './utils/prop'
import { getActivityPhase, maxPurchaseQuantity } from './utils/activityState'
import { createShoppingTracker } from './utils/track'

const store = useStore()
const router = useRouter()
const { t } = useI18n()
const tracker = createShoppingTracker(useLog())
const cssType = isMobile ? 'mobile' : 'pc'

const commonConfig = reactive({
  activityStartTime: '',
  rechargeStatStartTime: '',
  rechargeStatEndTime: '',
  activityPageCloseTime: '',
  goods: [],
  milestones: [],
  serverTime: 0,
})

const activityInfo = reactive({
  voucherBalance: 0,
  totalDiamond: 0,
  voucherSpent: 0,
  goodsStatus: [],
  milestoneStatus: [],
  activityPhase: '',
})

const recordDialog = reactive({
  visible: false,
  type: 'purchase',
  list: [],
})

const resultDialog = reactive({
  visible: false,
  type: 'success',
  prop: null,
  rewards: [],
})

const actTimeDialog = reactive({
  visible: false,
  phase: 'not-start',
})

const todayKey = reactive({ value: gmt7DateKey() })
let requesting = false
let clockTimer = null
const clock = reactive({ base: 0, syncedAt: 0 })
const localNow = ref(Date.now())
const shownTimePhases = new Set()
const syncClock = (value) => {
  const parsed = parseActTimeToMs(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return
  clock.base = parsed
  clock.syncedAt = Date.now()
  localNow.value = clock.syncedAt
}

const loggedIn = computed(() => !!store.state.cpUserInfo?.cpUserId)
const cpUserId = computed(() => store.state.cpUserInfo?.cpUserId || '')
const voucherBalance = computed(() => Number(activityInfo.voucherBalance) || 0)
const voucherSpent = computed(() => Number(activityInfo.voucherSpent) || 0)

const trackContext = () => ({
  current_amount: Number(activityInfo.totalDiamond) || 0,
  account_id: cpUserId.value,
  aggregation_id: store.state.cpUserInfo?.accountNum || '',
  region_id: localeToRegionIdMap[store.state.locale],
  language: store.state.locale,
})
let pageEntryTracked = false
let disposed = false
let pendingLogin = null
const trackPageEntry = () => {
  if (disposed || pageEntryTracked) return
  pageEntryTracked = true
  tracker.pageEntry(trackContext())
}

const serverMs = computed(() => clock.base ? clock.base + (localNow.value - clock.syncedAt) : 0)
const startMs = computed(() => {
  const ms = parseActTimeToMs(commonConfig.activityStartTime)
  return Number.isNaN(ms) ? 0 : ms
})
const endMs = computed(() => {
  const ms = parseActTimeToMs(commonConfig.activityPageCloseTime)
  return Number.isNaN(ms) ? 0 : ms
})

const activityPhase = computed(() => getActivityPhase(serverMs.value, startMs.value, endMs.value))

// 每日商品：整期列表 + 当日一条（固定置于商品网格首位）
const dailyGoods = computed(() => commonConfig.goods
  .filter(item => item.goodsType === 'daily')
  .slice()
  .sort((a, b) => String(a.activeDate).localeCompare(String(b.activeDate))))

/**
 * 取「今天」的每日商品，作为网格第一格，每天自动替换。
 * - 有当天配置 → 用当天
 * - 活动开始前（当天没有配置）→ 用最近的一期，避免首格空白
 * - 活动结束后 → 用最后一期
 */
const todayDaily = computed(() => {
  const list = dailyGoods.value
  if (!list.length) return null
  const key = todayKey.value
  const exact = list.find(item => String(item.activeDate) === key)
  if (exact) return exact
  return list.find(item => String(item.activeDate) >= key) || list[list.length - 1]
})

const normalGoods = computed(() => commonConfig.goods
  .filter(item => item.goodsType === 'normal')
  .slice()
  .sort((a, b) => Number(a.price) - Number(b.price)))

const gridGoods = computed(() => [todayDaily.value, ...normalGoods.value].filter(Boolean))

const goodsStatusMap = computed(() => {
  const map = {}
  ;(activityInfo.goodsStatus || []).forEach(item => {
    map[item.goodsCode] = item
  })
  return map
})

const milestoneList = computed(() => {
  const statusMap = {}
  ;(activityInfo.milestoneStatus || []).forEach(item => {
    statusMap[item.rewardCode] = item.status
  })
  return (commonConfig.milestones || []).map(item => ({
    ...item,
    status: statusMap[item.rewardCode] || 0,
  }))
})

// ---------- 数据加载 ----------

const loadCommonConfig = async () => {
  try {
    const res = await NovShoppingApi.getCommonConfig({ _loading: 0 })
    if (res) {
      Object.assign(commonConfig, res)
      syncClock(res.serverTime)
    }
  } catch (e) {
    console.error(e)
  }
}

let activityRequest = null
let activityRequestUser = ''
const loadActivityInfo = async () => {
  if (!loggedIn.value) {
    Object.assign(activityInfo, {
      voucherBalance: 0,
      totalDiamond: 0,
      voucherSpent: 0,
      goodsStatus: [],
      milestoneStatus: [],
      activityPhase: '',
    })
    return
  }
  const userId = cpUserId.value
  if (activityRequest && activityRequestUser === userId) return activityRequest
  activityRequestUser = userId
  const request = (async () => {
    try {
      const res = await NovShoppingApi.getActivityInfo({ cpUserId: userId })
      if (res && !disposed && cpUserId.value === userId) {
        Object.assign(activityInfo, res)
        syncClock(res.serverTime)
        trackPageEntry()
        if (pendingLogin?.account_id === userId) {
          const context = pendingLogin
          pendingLogin = null
          tracker.login({ ...context, current_amount: Number(res.totalDiamond) || 0 })
        }
        return res
      }
    } catch (e) {
      console.error(e)
    }
  })()
  activityRequest = request
  try {
    return await request
  } finally {
    if (activityRequest === request) activityRequest = null
  }
}

const refreshPageData = async () => {
  await Promise.all([loadCommonConfig(), loadActivityInfo()])
}

// 每种时间状态只自动提示一次，边界变化仍立即更新按钮并刷新用户状态。
const syncActTimeDialog = () => {
  const phase = activityPhase.value
  if (!['not-start', 'ended'].includes(phase) || shownTimePhases.has(phase)) return
  shownTimePhases.add(phase)
  actTimeDialog.phase = phase
  actTimeDialog.visible = true
}

const allowAction = () => {
  if (activityPhase.value === 'active') return true
  if (activityPhase.value !== 'loading') {
    actTimeDialog.phase = activityPhase.value
    actTimeDialog.visible = true
  }
  return false
}

watch(activityPhase, (phase, previous) => {
  syncActTimeDialog()
  if (phase === 'active' && previous === 'not-start') loadActivityInfo()
})
watch(() => serverMs.value && gmt7DateKey(serverMs.value), (day) => {
  if (!day || day === todayKey.value) return
  todayKey.value = day
  loadActivityInfo()
})

// ---------- 交互 ----------

const goRecharge = () => {
  tracker.rechargeClick(trackContext())
  const resolved = router.resolve({ name: 'Payment' })
  window.open(resolved.href, '_blank')
}

const scrollToVerify = () => {
  document.getElementById('nov-shopping-verify')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const errorCodeOf = (error) => Number(error?.code ?? error?.data?.code ?? 0)

const showNotEnough = () => {
  resultDialog.rewards = []
  resultDialog.type = 'not-enough'
  resultDialog.prop = null
  resultDialog.visible = true
}

const handleExchange = async ({ goods, count }) => {
  if (requesting || !allowAction()) return
  if (!loggedIn.value) {
    scrollToVerify()
    return
  }
  if (!goods || !Number.isSafeInteger(count) || count <= 0) return
  const maximum = maxPurchaseQuantity(goods, goodsStatusMap.value[goods.goodsCode], voucherBalance.value)
  if (count > maximum) {
    if (voucherBalance.value < Number(goods.price) * count) showNotEnough()
    await loadActivityInfo()
    return
  }
  requesting = true
  const context = trackContext()
  const trackGoods = { ...goods, propName: getPropName(t, goods) }
  try {
    await NovShoppingApi.purchase({
      cpUserId: context.account_id,
      goodsCode: goods.goodsCode,
      count,
    })
    tracker.exchange(context, trackGoods, count)
    resultDialog.rewards = []
    resultDialog.type = 'success'
    resultDialog.prop = {
      propId: goods.propId,
      propName: getPropName(t, goods),
      propCount: Number(goods.propCount) * count,
    }
    resultDialog.visible = true
    await loadActivityInfo()
  } catch (error) {
    const code = errorCodeOf(error)
    if (code === 4101) {
      showNotEnough()
    } else if (code === 4103) {
      resultDialog.rewards = []
      resultDialog.type = 'expired'
      resultDialog.prop = null
      resultDialog.visible = true
      await refreshPageData()
    }
    if (code === 2001 || code === 2002) {
      actTimeDialog.phase = code === 2001 ? 'not-start' : 'ended'
      actTimeDialog.visible = true
    }
    await loadActivityInfo()
    console.error('purchase error', error)
  } finally {
    requesting = false
  }
}

const handleClaim = async (rewardCode) => {
  if (requesting || !allowAction()) return
  if (!loggedIn.value) { scrollToVerify(); return }
  requesting = true
  const context = trackContext()
  try {
    const res = await NovShoppingApi.receiveMilestone({
      cpUserId: context.account_id,
      rewardCode,
    })
    tracker.milestones(context, res?.milestones)
    showClaimSuccess(res?.props || [])
    await loadActivityInfo()
  } catch (error) {
    await refreshPageData()
    console.error('milestone receive error', error)
  } finally {
    requesting = false
  }
}

const handleClaimAll = async () => {
  if (requesting || !allowAction()) return
  if (!loggedIn.value) { scrollToVerify(); return }
  requesting = true
  const context = trackContext()
  try {
    const res = await NovShoppingApi.receiveMilestone({ cpUserId: context.account_id })
    tracker.milestones(context, res?.milestones)
    showClaimSuccess(res?.props || [], true)
    await loadActivityInfo()
  } catch (error) {
    await refreshPageData()
    console.error('milestone claim all error', error)
  } finally {
    requesting = false
  }
}

const showClaimSuccess = (rewards, claimAll = false) => {
  resultDialog.rewards = rewards
  resultDialog.type = isMobile && claimAll ? 'claim-all-success' : 'claim-success'
  resultDialog.prop = null
  resultDialog.visible = true
}

const openPurchaseRecord = async () => {
  if (!loggedIn.value) {
    scrollToVerify()
    return
  }
  try {
    const res = await NovShoppingApi.getPurchaseRecord({ cpUserId: cpUserId.value })
    recordDialog.list = res?.list || []
  } catch (error) {
    console.error('purchase record error', error)
    recordDialog.list = []
  }
  recordDialog.type = 'purchase'
  recordDialog.visible = true
}

const openMilestoneRecord = async () => {
  if (!loggedIn.value) {
    scrollToVerify()
    return
  }
  try {
    const res = await NovShoppingApi.getMilestoneRecord({ cpUserId: cpUserId.value })
    recordDialog.list = res?.list || []
  } catch (error) {
    console.error('milestone record error', error)
    recordDialog.list = []
  }
  recordDialog.type = 'milestone'
  recordDialog.visible = true
}

// ---------- 生命周期 ----------

// 只监听本次真正完成的登录提交；缓存恢复、数据刷新不算登录。
// 同时覆盖角色确认和支付站 uid 链接自动登录，同账号重新输入也会上报。
const unsubscribeLogin = store.subscribe((mutation, state) => {
  if (mutation.type !== 'SET_CP_USER_INFO') return
  pendingLogin = state.cpUserInfo?.cpUserId ? trackContext() : null
  // 活动态失败时保留待报登录，下一次刷新成功再取真实累充，避免把未知金额报成 0。
  return loadActivityInfo()
})

watch(() => store.state.cpUserInfo?.cpUserId, () => {
  loadActivityInfo()
})

watch(() => store.state.locale, () => {
  refreshPageData()
})

const handleVisibilityChange = () => {
  if (!document.hidden) {
    refreshPageData()
  }
}

onMounted(() => {
  clockTimer = setInterval(() => { localNow.value = Date.now() }, 1000)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  if (!loggedIn.value) trackPageEntry()
  refreshPageData()
})

onUnmounted(() => {
  disposed = true
  unsubscribeLogin()
  clearInterval(clockTimer)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style lang="less" scoped src="./pc_style.less"></style>
<style lang="less" scoped src="./mobile_style.less"></style>
<!-- 跨组件复用的 PC 设计原语（sp- 前缀），不加 scoped 才能作用于子组件 -->
<style lang="less" src="./styles/shared.less"></style>
<style lang="less" src="./styles/loading.less"></style>
